import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../context/LanguageContext';
import './Events.css';

const MONTHS = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  cs: ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'],
};

function parseDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s || '');
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
}

export default function Events() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    fetch('/api/verunka/events')
      .then(r => r.json())
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const closeDetail = useCallback(() => setSelected(null), []);
  const imgs = (selected && selected.images) || [];

  useEffect(() => {
    if (!selected) return;
    const h = (e) => {
      if (e.key === 'Escape') closeDetail();
      else if (e.key === 'ArrowLeft' && imgs.length) setImgIdx(i => (i - 1 + imgs.length) % imgs.length);
      else if (e.key === 'ArrowRight' && imgs.length) setImgIdx(i => (i + 1) % imgs.length);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [selected, imgs.length, closeDetail]);

  // API returns events sorted by date ascending; keep only upcoming (closest first).
  const now = new Date();
  const upcoming = events.filter(e => { const d = parseDate(e.date); return d && d >= now; });

  if (!upcoming.length) return null;

  const fmt = (s) => {
    const d = parseDate(s);
    if (!d) return s;
    const time = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    return lang === 'cs'
      ? `${d.getDate()}. ${MONTHS.cs[d.getMonth()]} ${d.getFullYear()} · ${time}`
      : `${MONTHS.en[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${time}`;
  };

  const titleOf = (e) => (e.title && (e.title[lang] || e.title.en)) || e.id || '';
  const openDetail = (e) => { setSelected(e); setImgIdx(0); };
  const prevImg = () => setImgIdx(i => (i - 1 + imgs.length) % imgs.length);
  const nextImg = () => setImgIdx(i => (i + 1) % imgs.length);
  const desc = selected ? ((selected.desc && (selected.desc[lang] || selected.desc.en)) || []) : [];

  return (
    <section id="exhibition" className="events">
      <div className="events__inner">
        <div className="section-label">{t.exhibition.label}</div>

        <ul className="evt-list">
          {upcoming.map((e, i) => (
            <li key={i}>
              <button className="evt-list__item" onClick={() => openDetail(e)}>
                <span className="evt-list__date">{fmt(e.date)}</span>
                <span className="evt-list__name">{titleOf(e)}</span>
                {e.venue && <span className="evt-list__venue">{e.venue}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {selected && (
        <div className="evt-modal-overlay" onClick={closeDetail} role="dialog" aria-modal="true">
          <div className="evt-modal" onClick={e => e.stopPropagation()}>
            <button className="evt-modal__close" onClick={closeDetail} aria-label="Close">&#x2715;</button>
            {imgs.length > 0 && (
              <div className="evt-carousel">
                <img src={imgs[imgIdx]} alt={titleOf(selected)} />
                {imgs.length > 1 && (
                  <>
                    <button className="evt-carousel__nav evt-carousel__nav--prev" onClick={prevImg} aria-label="Previous">&lsaquo;</button>
                    <button className="evt-carousel__nav evt-carousel__nav--next" onClick={nextImg} aria-label="Next">&rsaquo;</button>
                    <span className="evt-carousel__count">{imgIdx + 1} / {imgs.length}</span>
                  </>
                )}
              </div>
            )}
            <div className="evt-modal__body">
              <p className="evt-modal__date">{fmt(selected.date)}</p>
              <h3 className="evt-modal__name">{titleOf(selected)}</h3>
              {selected.venue && (
                <p className="evt-modal__where">
                  <span className="evt-modal__venue">{selected.venue}</span>
                  {selected.address && <span className="evt-modal__addr">{selected.address}</span>}
                </p>
              )}
              {desc.map((p, i) => <p key={i} className="evt-modal__desc">{p}</p>)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
